import type { AdminGuide } from "../adminGuides.types";
import { GUIDE_SOURCE_DOCS } from "./sourceDocs";

export const crmDuplicatesAndIdentityReviewGuide: AdminGuide = {
  id: "crm-duplicates-and-identity-review",
  title: "CRM duplicates and identity review",
  category: "Customer Support",
  audience: ["Support", "Ops", "CS", "Non-technical"],
  summary:
    "Understand the difference between users, contacts, organisations, and partner clients before you change CRM records.",
  keywords: [
    "crm",
    "duplicates",
    "identity review",
    "merge contacts",
    "combine contacts",
    "billing contact",
    "primary contact",
    "user account",
    "organisation",
    "contact roles",
  ],
  location: "Admin",
  risk: "high",
  sourceDocs: [
    GUIDE_SOURCE_DOCS.CRM_OWNERSHIP_MODEL,
    GUIDE_SOURCE_DOCS.CRM_MIGRATION_AND_CUTOVER,
    GUIDE_SOURCE_DOCS.CRM_RELATIONSHIP_EXPLAINER,
  ],
  // Keep this guide route-focused and jargon-light because Sprint 3 links to
  // it from multiple operator entrypoints, not just the dedicated CRM pages.
  warnings: [
    "High risk: combining archives one contact and keeps one contact live. Read the preview before you confirm.",
    "Do not combine the contacts when the preview says they point to different active app users.",
    "Use identity review when an email already belongs to another live contact or when the source data is unclear.",
    "When the duplicate preview shows a hard block, the follow-up item also appears in Identity review so another operator can pick it up later.",
  ],
  safetyNotes: [
    "A user is the HelloCashflow person identity tied to one or more organisations. A CRM contact is the CRM person record. They are related, but they are not the same record.",
    "An organisation can point to any live CRM contact as primary contact or billing contact, including a contact who does not currently have app access for that organisation.",
    "Combined duplicates are archived and point to the kept contact. They are kept for history and audit.",
    "If a Landing form contact was combined with the contact made when they signed in with a different email, **Separate these contacts** on either contact's record undoes it.",
  ],
  whatThisHelpsYouDo: [
    "Choose the correct place to work: duplicate queue vs identity review.",
    "Read the preview in plain English before changing live records.",
    "Explain to non-technical staff why warnings appear and what they should do next.",
  ],
  whenToUse: [
    "Two contact records appear to describe the same person.",
    "Combining is blocked because the linked user or email outcome is unsafe.",
    "Support/admin users need to understand primary contact, billing contact, and linked-user warnings.",
  ],
  whenNotToUse: [
    "You only need to edit normal contact details such as notes, tags, or lifecycle stage.",
    "You already know the issue is unrelated to identity and belongs in billing, onboarding, or provider troubleshooting.",
  ],
  beforeYouStart: [
    "Confirm which contact should stay live after combining.",
    "Confirm whether either contact has a linked HelloCashflow user.",
    "Check whether either contact is the current primary contact or billing contact for an organisation.",
    "Open identity review instead of guessing when the preview shows a hard block.",
  ],
  steps: [
    "Start on **CRM → Duplicates** when you think two contacts are the same person.",
    "Review the suggested queue first. Use manual pair search only when the pair is not already listed.",
    "Read the preview sections in order: person outcome, email outcome, linked app-user outcome, organisation contact roles, and archive outcome.",
    "If the preview shows only warnings, combine the records only when the outcome still matches the real person you expect to keep.",
    "If the preview shows a hard block, stop and open **CRM → Identity review**. Do not work around the block by guessing.",
    "Use **Identity review** for unclear sign-up matches, records brought over from the old system, email ownership conflicts, and references to outside systems that need checking.",
    "When a Landing form contact signed in with a different email, use **Combine these two** on the Identity review item. Keep the form contact when the signed-in contact is new; keep the signed-in contact when it already has its own history.",
    "Mark an external reference as linked only after you confirm it now points to the correct contact in the product.",
    "Mark the item resolved only after you can explain what changed in the product and why it is now safe.",
  ],
  decisionTable: [
    {
      situation:
        "Two contacts clearly describe the same person and the preview shows no hard block",
      use: "Duplicates",
      notes: "Review the preview, then combine the records.",
    },
    {
      situation:
        "The preview says the contacts point to different active app users",
      use: "Identity review",
      notes: "Do not combine until the linked-user conflict is resolved.",
    },
    {
      situation:
        "The preview says an email already belongs to another live contact",
      use: "Identity review",
      notes: "Do not guess which email ownership is correct.",
    },
    {
      situation: "You only need to change primary contact or billing contact",
      use: "Users or organisation details",
      notes:
        "Contact roles are organisation-owned choices, not a way to combine duplicates.",
    },
  ],
  whatSuccessLooksLike: [
    "The surviving contact is the one you expected to keep.",
    "Warnings were reviewed instead of ignored.",
    "Hard blocks were resolved through identity review instead of by guessing.",
    "Support/admin users can explain why a contact can exist without a linked user and why primary or billing contact roles can stay with a contact who does not currently have app access for that organisation.",
  ],
  faqs: [
    {
      title: "What is the difference between a user and a contact?",
      body: [
        "A user is the HelloCashflow person identity tied to one or more organisations.",
        "A CRM contact is the person record used by support and admin workflows.",
        "A person can be a contact, a user, or both.",
      ],
    },
    {
      title:
        "Why can a warning say the contact does not currently have app access?",
      body: [
        "Primary contact and billing contact are organisation-owned roles.",
        "A contact can stay valid for those roles even when they do not currently have app access for that organisation.",
      ],
    },
    {
      title: "What does combining do to the duplicate contact?",
      body: [
        "Combining keeps one live contact.",
        "The duplicate is archived and points to the survivor so the history is still visible.",
      ],
    },
  ],
  escalateWhen: [
    "You cannot tell which contact should stay live.",
    "The preview shows a hard block you do not understand.",
    "The linked-user or email state still looks wrong after identity review.",
    "A contact is tied to sensitive billing or access work and the outcome is not obvious.",
  ],
  relatedAdminPaths: [
    { label: "CRM duplicates", path: "/crm/duplicates" },
    { label: "CRM identity review", path: "/crm/identity-review" },
    { label: "Users", path: "/users" },
    { label: "CRM contacts", path: "/crm" },
  ],
};
